import { useState, useEffect } from "react";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";
import { PRODUCTS } from "@/lib/site";

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  country: "",
  product_interest: "",
  message: "",
};

export default function QuoteForm({ compact = false, defaultProductInterest = "", defaultMessage = "" }) {
  const [form, setForm] = useState({ ...initial, product_interest: defaultProductInterest, message: defaultMessage });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (defaultProductInterest) {
      setForm((f) => ({ ...f, product_interest: defaultProductInterest }));
    }
    if (defaultMessage) {
      setForm((f) => ({ ...f, message: defaultMessage }));
    }
  }, [defaultProductInterest, defaultMessage]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill name, email, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry({ ...form, source: "website" });
      toast.success("Thank you! Our team will reach out within 24 hours.");
      setDone(true);
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="rounded-2xl border border-stenzia-green/20 bg-stenzia-green/5 p-10 text-center">
        <CheckCircle2 className="mx-auto text-stenzia-green" size={48} />
        <h3 className="mt-4 font-display text-2xl text-charcoal">Inquiry received</h3>
        <p className="mt-2 text-charcoal/70 max-w-md mx-auto">
          Our engineering team will be in touch within 24 hours. For urgent requests, message us on WhatsApp.
        </p>
        <button
          data-testid="quote-send-another"
          onClick={() => setDone(false)}
          className="mt-6 btn-secondary text-sm"
          style={{ padding: "10px 22px" }}
        >
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="quote-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div className="flex flex-col gap-2">
        <Label htmlFor="qf-name" className="text-xs font-semibold tracking-wide uppercase text-charcoal/70">Full Name *</Label>
        <Input
          id="qf-name"
          data-testid="qf-name"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Your name"
          className="h-12 rounded-xl qf-input"
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="qf-email" className="text-xs font-semibold tracking-wide uppercase text-charcoal/70">Email *</Label>
        <Input
          id="qf-email"
          data-testid="qf-email"
          type="email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="you@company.com"
          className="h-12 rounded-xl qf-input"
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="qf-phone" className="text-xs font-semibold tracking-wide uppercase text-charcoal/70">Phone *</Label>
        <Input
          id="qf-phone"
          data-testid="qf-phone"
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          placeholder="+91 99253 55561"
          className="h-12 rounded-xl qf-input"
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="qf-company" className="text-xs font-semibold tracking-wide uppercase text-charcoal/70">Company</Label>
        <Input
          id="qf-company"
          data-testid="qf-company"
          value={form.company}
          onChange={(e) => update("company", e.target.value)}
          placeholder="Your company"
          className="h-12 rounded-xl qf-input"
        />
      </div>

      {!compact && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="qf-country" className="text-xs font-semibold tracking-wide uppercase text-charcoal/70">Country</Label>
          <Input
            id="qf-country"
            data-testid="qf-country"
            value={form.country}
            onChange={(e) => update("country", e.target.value)}
            placeholder="India / UAE / USA …"
            className="h-12 rounded-xl qf-input"
          />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label className="text-xs font-semibold tracking-wide uppercase text-charcoal/70">
          Product Interest
          {defaultProductInterest && (
            <span className="ml-2 text-[10px] tracking-wide uppercase text-stenzia-green/80 font-medium">
              · Auto-detected
            </span>
          )}
        </Label>
        <Select value={form.product_interest} onValueChange={(v) => update("product_interest", v)}>
          <SelectTrigger data-testid="qf-product" className="h-12 rounded-xl qf-input">
            <SelectValue placeholder="Select a product category" />
          </SelectTrigger>
          <SelectContent>
            {defaultProductInterest && !PRODUCTS.some((p) => p.name === defaultProductInterest) && (
              <SelectItem value={defaultProductInterest} data-testid="qf-product-detected">
                {defaultProductInterest}
              </SelectItem>
            )}
            {PRODUCTS.map((p) => (
              <SelectItem key={p.slug} value={p.name} data-testid={`qf-product-${p.slug}`}>{p.name}</SelectItem>
            ))}
            <SelectItem value="General Inquiry" data-testid="qf-product-general">General Inquiry</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2 flex flex-col gap-2">
        <Label htmlFor="qf-message" className="text-xs font-semibold tracking-wide uppercase text-charcoal/70">Message *</Label>
        <Textarea
          id="qf-message"
          data-testid="qf-message"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Tell us about your showroom, required units, timelines…"
          rows={5}
          className="rounded-xl qf-input"
        />
      </div>

      <div className="md:col-span-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mt-2">
        <p className="text-xs text-charcoal/50">By submitting, you agree to be contacted by our team about your inquiry.</p>
        <button type="submit" disabled={loading} data-testid="qf-submit" className="btn-primary">
          {loading ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>) : (<>Request Quote <ArrowUpRight size={16} /></>)}
        </button>
      </div>
    </form>
  );
}
